/**
 * Events Page Component
 *
 * Events page for the National Council for Equal Shared Parenting.
 * Displays information about NCESP events and embeds a Google Calendar
 * for visitors to browse upcoming meetings, webinars, and advocacy actions.
 */

import React from 'react'
import { Box, Container, Typography, Stack, Link } from '@mui/material'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import SEOMetaTags from '@/components/SEOMetaTags'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'

const GOOGLE_CALENDAR_EMBED_URL =
  'https://calendar.google.com/calendar/embed?src=c_010eb56675ed0e0dbffd6922cea790946625b1e9c65e397b7d250ee468bb91b1%40group.calendar.google.com&ctz=America%2FNew_York'

/**
 * Events Page Component
 */
const Events: React.FC = () => {
  const seoConfig = getSEOConfig('/events')!
  const canonicalUrl = getCanonicalUrl('/events')

  return (
    <Box component="main" role="main">
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />
      <Container maxWidth="lg">
        <Stack spacing={4} sx={{ py: 8 }}>
          {/* Page Header */}
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              Events
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 800, mx: 'auto' }}
            >
              NCESP Events &amp; Calendar
            </Typography>
          </Box>

          {/* About Our Events */}
          <Box sx={{ py: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              About Our Events
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
            >
              The National Council for Equal Shared Parenting hosts a variety of
              events throughout the year to advance our mission of ensuring
              children maintain meaningful relationships with both parents. From
              legislative strategy meetings to public education webinars, our
              events bring together advocates, families, and policymakers
              committed to equal shared parenting reform.
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
            >
              Our events include regular community meetings, advocacy training
              sessions, webinars featuring expert speakers on family law reform,
              and coordinated action days where supporters across the country
              come together to make their voices heard.
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}
            >
              Browse the calendar below to find upcoming events and
              opportunities to get involved. All are welcome, whether you are
              new to the cause or a longtime advocate for children&apos;s
              rights.
            </Typography>
          </Box>

          {/* Google Calendar Embed */}
          <Box sx={{ py: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Upcoming Events
            </Typography>

            <Box role="region" aria-label="NCESP Events Calendar">
              <Box sx={{ width: '100%', position: 'relative' }}>
                <Box
                  component="iframe"
                  src={GOOGLE_CALENDAR_EMBED_URL}
                  title="NCESP Events Calendar"
                  width="100%"
                  loading="lazy"
                  sx={{
                    border: 0,
                    height: { xs: 400, sm: 500, md: 600, lg: 600 },
                  }}
                />
              </Box>
            </Box>

            {/* Fallback Link */}
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                href={GOOGLE_CALENDAR_EMBED_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                View calendar on Google Calendar
                <OpenInNewIcon fontSize="small" />
              </Link>
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}

export default Events
